import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { TrendingUpIcon, UsersIcon, CheckCircleIcon, ArrowRightIcon, ShieldCheckIcon, BookOpenIcon, TargetIcon, BriefcaseIcon, DollarSignIcon, StarIcon } from 'lucide-react';
import { CareerJourney } from '../components/CareerJourney';
import { CareerCalculator } from '../components/CareerCalculator';
export function Career() {
  const benefits = [{
    icon: ShieldCheckIcon,
    title: 'Sistem Verificat',
    description: 'Nu reinventezi roata. Avem o "rețetă" clară, testată, care garantează rezultate dacă o urmezi pas cu pas.',
    color: 'bg-blue-100 text-blue-600'
  }, {
    icon: DollarSignIcon,
    title: 'Câștiguri Reale',
    description: 'Comisioane atractive și transparente. Veniturile tale cresc direct proporțional cu implicarea ta.',
    color: 'bg-green-100 text-green-600'
  }, {
    icon: BookOpenIcon,
    title: 'Training Complet',
    description: 'Nu ai nevoie de experiență. Îți oferim acces la cursuri, mentorat și toate resursele necesare pentru a deveni expert.',
    color: 'bg-purple-100 text-purple-600'
  }, {
    icon: TargetIcon,
    title: 'Foarte Ușor de Început',
    description: 'Proces de onboarding simplificat. În câteva zile poți avea deja primele vizionări și primele tranzacții.',
    color: 'bg-orange-100 text-orange-600'
  }];
  const steps = [{
    number: '01',
    title: 'Înregistrează-te',
    description: 'Creează-ți contul în platformă. Este primul pas către independența ta financiară.'
  }, {
    number: '02',
    title: 'Învață "Rețeta"',
    description: 'Parcurge cursurile noastre interactive. Îți explicăm exact ce ai de făcut, pas cu pas.'
  }, {
    number: '03',
    title: 'Aplică și Câștigă',
    description: 'Folosește instrumentele noastre digitale pentru a găsi clienți și a încheia tranzacții.'
  }];
  return <div className="w-full min-h-screen bg-[#F1F1F1]" data-id="element-7330">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-[#132C53] to-[#1e4278] text-white overflow-hidden" data-id="element-7331">
        <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]" data-id="element-7332"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32 relative z-10" data-id="element-7333">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-7334">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.6
          }} data-id="element-7335">
              <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6 border border-white/20" data-id="element-7336">
                <StarIcon className="w-4 h-4 text-yellow-400 fill-current" data-id="element-7337" />
                <span className="text-sm font-medium text-white" data-id="element-7338">
                  Cea mai rapidă cale către succes în imobiliare
                </span>
              </div>
              <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight" data-id="element-7339">
                Devino Agent și <br data-id="element-7340" />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#357BEB] to-[#21A366]" data-id="element-7341">
                  Preia Controlul
                </span>{' '}
                Veniturilor Tale
              </h1>
              <p className="text-xl text-gray-300 mb-8 max-w-lg leading-relaxed" data-id="element-7342">
                Avem rețeta succesului și vrem să ți-o oferim. Este mai ușor
                decât crezi să începi o carieră profitabilă în imobiliare, chiar
                și fără experiență anterioară.
              </p>
              <div className="flex flex-col sm:flex-row gap-4" data-id="element-7343">
                <Link to="/auth/register" className="inline-flex items-center justify-center px-8 py-4 text-lg font-bold rounded-2xl text-white bg-gradient-to-r from-[#357BEB] to-[#21A366] hover:shadow-lg hover:scale-105 transition-all duration-300 group" data-id="element-7344">
                  Devino Agent
                  <ArrowRightIcon className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-7345" />
                </Link>
                <a href="#cum-functioneaza" className="inline-flex items-center justify-center px-8 py-4 text-lg font-bold rounded-2xl text-white border-2 border-white/20 hover:bg-white/10 transition-all" data-id="element-7346">
                  Cum funcționează?
                </a>
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="relative hidden lg:block" data-id="element-7347">
              {/* Abstract visual representation of success/growth */}
              <div className="relative z-10 bg-white/5 backdrop-blur-md rounded-3xl p-8 border border-white/10 shadow-2xl" data-id="element-7348">
                <div className="flex items-center justify-between mb-8" data-id="element-7349">
                  <div data-id="element-7350">
                    <p className="text-gray-400 text-sm" data-id="element-7351">Venituri estimate</p>
                    <h3 className="text-3xl font-bold text-white" data-id="element-7352">
                      €4,500<span className="text-lg text-gray-400" data-id="element-7353">/lună</span>
                    </h3>
                  </div>
                  <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center" data-id="element-7354">
                    <TrendingUpIcon className="w-6 h-6 text-green-400" data-id="element-7355" />
                  </div>
                </div>
                <div className="space-y-4" data-id="element-7356">
                  <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl border border-white/5" data-id="element-7357">
                    <div className="flex items-center space-x-3" data-id="element-7358">
                      <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center" data-id="element-7359">
                        <UsersIcon className="w-5 h-5 text-blue-400" data-id="element-7360" />
                      </div>
                      <div data-id="element-7361">
                        <p className="font-semibold text-white" data-id="element-7362">Leaduri Noi</p>
                        <p className="text-xs text-gray-400" data-id="element-7363">Săptămâna asta</p>
                      </div>
                    </div>
                    <span className="text-xl font-bold text-white" data-id="element-7364">+12</span>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl border border-white/5" data-id="element-7365">
                    <div className="flex items-center space-x-3" data-id="element-7366">
                      <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center" data-id="element-7367">
                        <BriefcaseIcon className="w-5 h-5 text-purple-400" data-id="element-7368" />
                      </div>
                      <div data-id="element-7369">
                        <p className="font-semibold text-white" data-id="element-7370">Tranzacții</p>
                        <p className="text-xs text-gray-400" data-id="element-7371">În procesare</p>
                      </div>
                    </div>
                    <span className="text-xl font-bold text-white" data-id="element-7372">3</span>
                  </div>
                </div>
                <div className="mt-8 pt-6 border-t border-white/10" data-id="element-7373">
                  <div className="flex items-center space-x-2 text-sm text-gray-300" data-id="element-7374">
                    <CheckCircleIcon className="w-4 h-4 text-green-400" data-id="element-7375" />
                    <span data-id="element-7376">Sistem complet digitalizat</span>
                  </div>
                  <div className="flex items-center space-x-2 text-sm text-gray-300 mt-2" data-id="element-7377">
                    <CheckCircleIcon className="w-4 h-4 text-green-400" data-id="element-7378" />
                    <span data-id="element-7379">Suport dedicat 24/7</span>
                  </div>
                </div>
              </div>
              {/* Decorative elements */}
              <div className="absolute -top-10 -right-10 w-32 h-32 bg-[#357BEB] rounded-full blur-3xl opacity-30" data-id="element-7380"></div>
              <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-[#21A366] rounded-full blur-3xl opacity-30" data-id="element-7381"></div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20" data-id="element-7382">
        <div className="text-center mb-16" data-id="element-7383">
          <h2 className="text-3xl md:text-4xl font-bold text-[#132C53] mb-4" data-id="element-7384">
            De ce să alegi Cautlocuință.ro?
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-7385">
            Nu îți oferim doar un job, ci un ecosistem complet construit pentru
            succesul tău.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-7386">
          {benefits.map((benefit, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: index * 0.1
        }} className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 group" data-id="element-7387">
              <div className={`w-14 h-14 rounded-2xl ${benefit.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`} data-id="element-7388">
                <benefit.icon className="w-7 h-7" data-id="element-7389" />
              </div>
              <h3 className="text-xl font-bold text-[#132C53] mb-3" data-id="element-7390">
                {benefit.title}
              </h3>
              <p className="text-gray-600 leading-relaxed" data-id="element-7391">
                {benefit.description}
              </p>
            </motion.div>)}
        </div>
      </div>

      {/* How it works Section */}
      <div id="cum-functioneaza" className="bg-white py-20" data-id="element-7392">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-7393">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-7394">
            <div data-id="element-7395">
              <h2 className="text-3xl md:text-4xl font-bold text-[#132C53] mb-6" data-id="element-7396">
                Avem Rețeta Succesului. <br data-id="element-7397" />
                <span className="text-[#357BEB]" data-id="element-7398">
                  Trebuie doar să o aplici.
                </span>
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed" data-id="element-7399">
                Am simplificat procesul de a deveni agent imobiliar. Nu te
                aruncăm în piață fără direcție. Îți oferim o hartă clară,
                instrumente digitale și suport constant.
              </p>

              <div className="space-y-8" data-id="element-7400">
                {steps.map((step, index) => <motion.div key={index} initial={{
                opacity: 0,
                x: -20
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.5,
                delay: index * 0.1
              }} className="flex items-start space-x-4" data-id="element-7401">
                    <div className="flex-shrink-0 w-12 h-12 bg-[#132C53] text-white rounded-full flex items-center justify-center font-bold text-lg shadow-md" data-id="element-7402">
                      {step.number}
                    </div>
                    <div data-id="element-7403">
                      <h3 className="text-xl font-bold text-[#132C53] mb-2" data-id="element-7404">
                        {step.title}
                      </h3>
                      <p className="text-gray-600" data-id="element-7405">{step.description}</p>
                    </div>
                  </motion.div>)}
              </div>

              <div className="mt-10" data-id="element-7406">
                <Link to="/auth/register" className="inline-flex items-center justify-center px-8 py-4 text-lg font-bold rounded-2xl text-white bg-[#132C53] hover:bg-[#2868d8] transition-all shadow-lg hover:shadow-xl" data-id="element-7407">
                  Începe Acum
                  <ArrowRightIcon className="ml-2 w-5 h-5" data-id="element-7408" />
                </Link>
              </div>
            </div>

            <div className="relative" data-id="element-7409">
              <div className="absolute inset-0 bg-gradient-to-br from-[#357BEB] to-[#21A366] rounded-3xl transform rotate-3 opacity-20" data-id="element-7410"></div>
              <img src="https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" alt="Team working together" className="relative rounded-3xl shadow-2xl w-full object-cover h-[600px]" data-id="element-7411" />
              <div className="absolute bottom-8 left-8 right-8 bg-white/90 backdrop-blur-md p-6 rounded-2xl shadow-lg border border-white/20" data-id="element-7412">
                <div className="flex items-center space-x-4" data-id="element-7413">
                  <div className="flex -space-x-3" data-id="element-7414">
                    {[1, 2, 3, 4].map(i => <div key={i} className="w-10 h-10 rounded-full border-2 border-white bg-gray-200 overflow-hidden" data-id="element-7415">
                        <img src={`https://i.pravatar.cc/100?img=${i + 10}`} alt="Agent" className="w-full h-full object-cover" data-id="element-7416" />
                      </div>)}
                  </div>
                  <div data-id="element-7417">
                    <p className="font-bold text-[#132C53]" data-id="element-7418">
                      Alătură-te echipei
                    </p>
                    <p className="text-xs text-gray-600" data-id="element-7419">
                      Peste 500+ agenți activi
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Career Journey Section */}
      <div className="py-20 bg-[#F9FAFB]" data-id="element-7420">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-7421">
          <CareerJourney data-id="element-7422" />
        </div>
      </div>

      {/* Calculator Section */}
      <div className="py-20 bg-white" data-id="element-7423">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-7424">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-7425">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} data-id="element-7426">
              <h2 className="text-3xl md:text-4xl font-bold text-[#132C53] mb-6" data-id="element-7427">
                Calculează-ți Potențialul
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed" data-id="element-7428">
                La Cautlocuință.ro, transparența este cheia. Folosește
                calculatorul nostru pentru a vedea exact la ce nivel te poți
                situa și ce venituri poți genera în funcție de activitatea ta.
              </p>
              <ul className="space-y-4 mb-8" data-id="element-7429">
                <li className="flex items-center space-x-3 text-gray-700" data-id="element-7430">
                  <CheckCircleIcon className="w-5 h-5 text-[#21A366]" data-id="element-7431" />
                  <span data-id="element-7432">Sistem de comisionare progresiv</span>
                </li>
                <li className="flex items-center space-x-3 text-gray-700" data-id="element-7433">
                  <CheckCircleIcon className="w-5 h-5 text-[#21A366]" data-id="element-7434" />
                  <span data-id="element-7435">Bonusuri de performanță</span>
                </li>
                <li className="flex items-center space-x-3 text-gray-700" data-id="element-7436">
                  <CheckCircleIcon className="w-5 h-5 text-[#21A366]" data-id="element-7437" />
                  <span data-id="element-7438">Acces la leaduri premium pe măsură ce avansezi</span>
                </li>
              </ul>
            </motion.div>
            <div data-id="element-7439">
              <CareerCalculator data-id="element-7440" />
            </div>
          </div>
        </div>
      </div>

      {/* Final CTA Section */}
      <div className="bg-[#132C53] py-20 relative overflow-hidden" data-id="element-7441">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10" data-id="element-7442"></div>
        <div className="absolute top-0 right-0 w-64 h-64 bg-[#357BEB] rounded-full blur-3xl opacity-20 transform translate-x-1/2 -translate-y-1/2" data-id="element-7443"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#21A366] rounded-full blur-3xl opacity-20 transform -translate-x-1/2 translate-y-1/2" data-id="element-7444"></div>

        <div className="max-w-4xl mx-auto px-4 text-center relative z-10" data-id="element-7445">
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" data-id="element-7446">
            Ești gata să începi?
          </h2>
          <p className="text-xl text-gray-300 mb-10 max-w-2xl mx-auto" data-id="element-7447">
            Nu mai aștepta momentul perfect. Momentul este acum. Alătură-te
            comunității Cautlocuință.ro și construiește-ți viitorul.
          </p>
          <Link to="/auth/register" className="inline-flex items-center justify-center px-10 py-5 text-xl font-bold rounded-2xl text-white bg-gradient-to-r from-[#357BEB] to-[#21A366] hover:shadow-2xl hover:scale-105 transition-all duration-300 group" data-id="element-7448">
            Devino Agent
            <ArrowRightIcon className="ml-2 w-6 h-6 group-hover:translate-x-1 transition-transform" data-id="element-7449" />
          </Link>
          <p className="mt-6 text-sm text-gray-400" data-id="element-7450">
            Înregistrarea durează mai puțin de 2 minute.
          </p>
        </div>
      </div>
    </div>;
}